import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Pages } from '../Pages'
import { MenuItems } from '../enums/MenuItems'
import tenantApi from '../api/tenant/TenantApi'
import UserRolePill from '../components/UserRolePill'
import {
    UserDto,
    UserRoles,
    UserTenantMembershipDto,
} from '../api/tenant/models/UserDto'
import { UpdateUserMembershipReq } from '../api/tenant/models/updateUser/UpdateUserReq'
import PersonSearchModal from '../uiV2/person/search/PersonSearchModal'
import { PersonDto } from '../api/person/models/PersonDto'
import PersonUi from '../components/PersonUi'
import ToggleSwitch from '../components/ToggleSwitch'
import LayoutSingleView from '../components/layout/LayoutSingleView'

const ROLE_OPTIONS: UserRoles[] = [
    UserRoles.HM,
    UserRoles.TA,
    UserRoles.JOB_SEEKER,
]

function UpdateUser(): React.JSX.Element {
    const [isSearchPersonModalOpen, setIsSearchPersonModalOpen] =
        useState(false)
    const [linkedPerson, setLinkedPerson] = useState<PersonDto>()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const userId = searchParams.get('userId')
    const tenantIdFromParams = searchParams.get('tenantId')
    const [user, setUser] = useState<UserDto>()
    const [selectedRoles, setSelectedRoles] = useState<UserRoles[]>([])
    const [memberships, setMemberships] = useState<UserTenantMembershipDto[]>(
        [],
    )
    const [isProductAdmin, setIsProductAdmin] = useState<boolean>(false)

    const refreshUser = useCallback(async () => {
        if (!userId) {
            return
        }

        const user = (await tenantApi.getUser(userId)).user
        setUser(user)
        setMemberships(user.memberships || [])
        setIsProductAdmin(user.isProductAdmin)

        const targetTenantId = resolveTenantIdForUser(user, tenantIdFromParams)

        const activeMembership = (user.memberships || []).find(
            (membership) => membership.tenantId === targetTenantId,
        )

        setSelectedRoles(activeMembership?.roles || [])
        setLinkedPerson(user.person || undefined)
    }, [userId, tenantIdFromParams])

    useEffect(() => {
        refreshUser()
    }, [userId, refreshUser])

    const resolvedTenantIdForDisplay = resolveTenantIdForUser(
        user,
        tenantIdFromParams,
    )

    function handleRoleChange(
        event: React.ChangeEvent<HTMLInputElement>,
    ): void {
        const role = event.target.value as UserRoles
        if (event.target.checked) {
            setSelectedRoles((prev) => [...prev, role])
        } else {
            setSelectedRoles((prev) => prev.filter((r) => r !== role))
        }
    }

    function handleProductAdminChange(
        event: React.ChangeEvent<HTMLInputElement>,
    ): void {
        setIsProductAdmin(event.target.checked)
    }

    async function saveUser() {
        if (!user) {
            throw new Error(`Unable to save user, user not found.`)
        }

        const destinationTenantId = resolveTenantIdForUser(
            user,
            tenantIdFromParams,
        )

        const updatedMemberships = buildUpdatedMemberships(
            memberships,
            destinationTenantId,
            selectedRoles,
        )

        await tenantApi.updateUser({
            userId: user.id,
            isActive: user.isActive,
            memberships: updatedMemberships,
            isProductAdmin,
            personId: linkedPerson?.id,
        })
        if (destinationTenantId) {
            navigate(`${Pages.Tenant}?id=${destinationTenantId}`)
        }
    }

    async function handleSelectPerson(person: PersonDto) {
        setLinkedPerson(person)
        setIsSearchPersonModalOpen(false)
    }

    async function handleRemovePerson() {
        setLinkedPerson(undefined)
        setIsSearchPersonModalOpen(false)
    }

    function toggleActive() {
        if (user) {
            setUser({ ...user, isActive: !user.isActive })
        }
    }

    if (!user) {
        return (
            <LayoutSingleView activeMenuItem={MenuItems.Users}>
                <div>Loading...</div>
            </LayoutSingleView>
        )
    }

    return (
        <LayoutSingleView activeMenuItem={MenuItems.Users}>
            <div>
                <h1>
                    Edit User for{' '}
                    {user.memberships?.find(
                        (membership) =>
                            membership.tenantId === resolvedTenantIdForDisplay,
                    )?.tenant?.name ??
                        resolvedTenantIdForDisplay ??
                        'Unknown tenant'}
                </h1>
                <div>
                    <div>
                        <label htmlFor="email">Email*</label>
                        <input
                            id="email"
                            type="text"
                            placeholder="Email..."
                            value={user.email}
                            disabled
                            className="form-input"
                        ></input>
                    </div>
                    <div className="space-y-2 mt-4">
                        <div>
                            <label>
                                <input
                                    type="checkbox"
                                    id="admin"
                                    name="isProductAdmin"
                                    checked={isProductAdmin}
                                    onChange={handleProductAdminChange}
                                    className="mr-2"
                                />
                                <UserRolePill isProductAdmin />
                            </label>
                        </div>
                        {ROLE_OPTIONS.map((role) => (
                            <div key={role}>
                                <label>
                                    <input
                                        type="checkbox"
                                        id={role}
                                        name="roles"
                                        value={role}
                                        checked={selectedRoles.includes(role)}
                                        onChange={handleRoleChange}
                                        className="mr-2"
                                    />
                                    <UserRolePill value={role} />
                                </label>
                            </div>
                        ))}
                    </div>
                    <div className="mt-4">
                        <label htmlFor="isActive">Is Active</label>
                        <ToggleSwitch
                            checked={user.isActive}
                            onChange={toggleActive}
                        />
                    </div>
                    <div className="mt-4">
                        <label htmlFor="personId">
                            Link to Person (imported from Linked In)
                        </label>
                        {linkedPerson && (
                            <div>
                                <div>
                                    Selected person:{' '}
                                    <PersonUi person={linkedPerson} />
                                </div>
                                <button
                                    className="remove-button"
                                    onClick={handleRemovePerson}
                                >
                                    Remove
                                </button>
                            </div>
                        )}
                        <div>
                            <button
                                className="secondary-button"
                                onClick={() => setIsSearchPersonModalOpen(true)}
                            >
                                Select Person
                            </button>
                        </div>
                    </div>
                    <div className="form-buttons">
                        <button
                            className="primary-button mt-4"
                            onClick={saveUser}
                        >
                            Save
                        </button>
                        <button
                            className="danger-button"
                            onClick={() => {
                                const destinationTenantId =
                                    resolveTenantIdForUser(
                                        user,
                                        tenantIdFromParams,
                                    )

                                if (destinationTenantId) {
                                    navigate(
                                        `${Pages.Tenant}?id=${destinationTenantId}`,
                                    )
                                } else {
                                    navigate(Pages.Tenants)
                                }
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
                {isSearchPersonModalOpen && (
                    <PersonSearchModal
                        onClose={() => setIsSearchPersonModalOpen(false)}
                        onSelect={handleSelectPerson}
                    />
                )}
            </div>
        </LayoutSingleView>
    )
}

export default UpdateUser

function buildUpdatedMemberships(
    existingMemberships: UserTenantMembershipDto[],
    tenantId: string | undefined,
    roles: UserRoles[],
): UpdateUserMembershipReq[] {
    const result: UpdateUserMembershipReq[] = []
    const seenTenantIds = new Set<string>()

    if (tenantId) {
        result.push({ tenantId, roles })
        seenTenantIds.add(tenantId)
    }

    for (const membership of existingMemberships || []) {
        if (seenTenantIds.has(membership.tenantId)) {
            continue
        }

        result.push({
            tenantId: membership.tenantId,
            roles: membership.roles,
        })
        seenTenantIds.add(membership.tenantId)
    }

    return result
}

function resolveTenantIdForUser(
    user: UserDto | undefined,
    overrideTenantId: string | null,
): string | undefined {
    if (overrideTenantId) {
        return overrideTenantId
    }

    if (!user) {
        return undefined
    }

    if (user.activeTenantId) {
        return user.activeTenantId
    }

    return user.memberships?.find((membership) => membership.tenantId)?.tenantId
}
